/**
 * My time off (WALLIYO-0134).
 *
 * Every workspace's sidebar lists it under the rule, beside the directory: your
 * own leave belongs to you, not to the department you sit in, so it follows you
 * into whichever workspace is showing.
 *
 * It is the same view Human Resources uses, given only the half that is yours —
 * your balance, your requests, and who else is away when you are choosing a
 * week. Not a second implementation of the same screen: leave has one set of
 * rules and one place they are written, and a copy would drift from it.
 *
 * What it deliberately leaves out: leave waiting on **you** as somebody's lead,
 * and the whole company's. The first is on the Human Resources Overview, where
 * it belongs with everything else somebody is held up by; the second is HR's and
 * needs their capability.
 */

import type { TimeOffRequest } from "@wallyo/contracts/console/hr";
import { useEffect, useReducer, useRef, useState } from "react";

import { listTimeOff } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { useAlerts } from "../../shell/alerts";
import { PageTop } from "../../shell/PageTop";
import { AskTimeOffDialog } from "./TimeOffDialogs";
import { TimeOffView, type TimeOffState } from "./TimeOffView";

export function MyTimeOffScreen() {
  const { client } = useApi();
  const alerts = useAlerts();
  const [state, setState] = useState<TimeOffState>({ status: "loading", data: null });
  const [reads, read] = useReducer((count: number) => count + 1, 0);
  const [asking, setAsking] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setState((was) => ({ status: "loading", data: was.data }));
    listTimeOff(client).then(
      (data) => {
        if (generation.current !== mine) return;
        // Yours only. The endpoint answers with everything this person may see,
        // and this screen is the half that is theirs.
        setState({ status: "ready", data: { ...data, toDecide: [], everybody: [] } });
      },
      (error: unknown) =>
        generation.current === mine && setState((was) => ({ status: "failed", data: was.data, error })),
    );
  }, [client, reads]);

  return (
    <>
      <PageTop title={t("screen.myTimeOff")} sub={t("myTimeOff.sub")} />
      <TimeOffView
        state={state}
        onAsk={() => setAsking(true)}
        // Deciding somebody else's leave is not on this screen; the prop is the
        // shared view's, and here nothing can ask for it.
        onDecide={(_request: TimeOffRequest) => undefined}
        onRetry={read}
      />
      {asking ? (
        <AskTimeOffDialog
          onClose={() => setAsking(false)}
          onReload={() => {
            setAsking(false);
            read();
          }}
          onDone={(message) => {
            alerts.ok(message);
            setAsking(false);
            read();
          }}
        />
      ) : null}
    </>
  );
}
